import { useState } from 'react';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { fmtCents } from '@/components/money/format.js';

// MATCHING A STATEMENT'S LINES TO DEALS (NIGHT-13 R34, David 2026-11-02). TRACON proposes each item's deal (an item
// is the lines one deal accounts for: Skyward states hull, liability and the state fee as three lines). Items whose
// policy number agrees and whose amount agrees to the cent sit under ONE button; that click is the confirmation V4
// requires. Every other item has its own row saying in words why it is not exact, with its own Confirm and No such
// deal. Nothing is confirmed until a button is pressed. The proposal is the server's; this screen derives nothing.
// A line the carrier netted from two transactions (David 2026-11-03) is confirmed to both: the server proposes the pair
// whose nets and commissions add up to the line to the cent (item.split), or the broker names a second deal; the server
// refuses any pair that does not add up.

const selectCls = 'mt-1 h-10 w-full rounded-card border border-border bg-bg px-2 text-sm text-text';
const what = (x) => [x.txn_type, x.txn_effective_date ? `eff ${x.txn_effective_date}` : null].filter(Boolean).join(' · ');
// One deal in the picker (David 2026-11-03: two deals of one insured on one day read the same): name · carrier · policy number
// (or "no number yet") · type · eff day · premium.
const money = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? 'no premium stated' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const dealOption = ({ name, carrier, policy_number, type, effective_date, premium }) =>
  [name || 'deal', carrier || 'no carrier on file', policy_number || 'no number yet', type, effective_date ? `eff ${effective_date}` : null, money(premium)].filter(Boolean).join(' · ');
const itemLabel = (x) => `${x.insured_text}${x.invoice ? ` · ${x.invoice}` : x.policy_number ? ` · ${x.policy_number}` : ''}`;

function Figures({ refusal }) {
  return (
    <div className="mt-2 rounded-card border border-danger/40 bg-danger/5 p-2 font-mono text-xs text-text">
      net: carrier {fmtCents(refusal.stated?.net_due)} · TRACON {fmtCents(refusal.derived_net)} · difference {refusal.delta_net === null || refusal.delta_net === undefined ? 'TRACON cannot work it out' : fmtCents(refusal.delta_net)}
      <br />commission: carrier {fmtCents(refusal.stated?.commission)} · TRACON {fmtCents(refusal.derived_commission)}
    </div>
  );
}

function ItemRow({ statementId, item, unpaid, onDone }) {
  const choices = [
    ...(item.policy_transaction_id ? [{ id: item.policy_transaction_id, label: `${dealOption({ name: item.deal_name, carrier: item.txn_carrier_name, policy_number: item.txn_policy_number, type: item.txn_type, effective_date: item.txn_effective_date, premium: item.txn_premium })} (proposed)` }] : []),
    ...(item.others || []).map((o) => ({ id: o.policy_transaction_id, label: dealOption({ name: o.deal_name, carrier: o.carrier_name, policy_number: o.policy_number, type: o.type, effective_date: o.effective_date, premium: o.premium }) })),
    ...unpaid.filter((r) => r.id !== item.policy_transaction_id && !(item.others || []).some((o) => o.policy_transaction_id === r.id))
      .map((r) => ({ id: r.id, label: dealOption({ name: r.counterparty, carrier: r.carrier_name, policy_number: r.policy_number, type: r.type, effective_date: r.effective_date, premium: r.premium }) })),
  ];
  const [txn, setTxn] = useState(item.policy_transaction_id || '');
  const [txn2, setTxn2] = useState('');
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState(null);
  const [busy, setBusy] = useState(false);
  const chosen = choices.find((c) => c.id === txn);
  const needsReason = !!refusal || (txn && txn === item.policy_transaction_id && item.needs_reason);
  const label = (id) => (choices.find((c) => c.id === id) || {}).label;
  const decide = async (decision, splitIds = null) => {
    setBusy(true);
    try {
      const body = splitIds
        ? { decision, line_ids: item.line_ids, policy_transaction_ids: splitIds }
        : { decision, line_ids: item.line_ids, policy_transaction_id: decision === 'confirmed' ? txn : null, delta_reason: decision === 'confirmed' && reason.trim() ? reason.trim() : undefined };
      const r = await api.post(`/api/money/statements/${statementId}/items`, body);
      if (splitIds) {
        const pn = (r.policy_numbers || []).find((x) => x && x.recorded);
        toast.success(`${item.insured_text}: ${fmtCents(item.net_due)} confirmed to ${splitIds.length} transactions that add up to it.${pn ? ` Policy number ${pn.number} recorded from the statement.` : ''}`);
      } else if (decision === 'confirmed') {
        const leg = r.payable_leg;
        const pn = r.policy_number;
        toast.success(`${item.insured_text}: ${fmtCents(item.net_due)} confirmed to ${chosen ? chosen.label.replace(' (proposed)', '') : 'the deal'}.${leg ? (leg.opened ? ' Payable to the carrier opened.' : ` Payable not opened: ${leg.why}.`) : ''}${pn && pn.recorded ? ` Policy number ${pn.number} recorded from the statement.` : ''}`);
        if (pn && !pn.recorded && /refused|could not/.test(pn.why || '')) toast.info(`Policy number not recorded: ${pn.why}.`);
      } else toast.success(`${item.insured_text}: marked as no such deal.`);
      setRefusal(null);
      await onDone();
    } catch (e) {
      if (e.status === 409 && e.body?.code === 'DELTA_REASON_REQUIRED') { setRefusal(e.body); return; }
      if (e.body?.code === 'SPLIT_DOES_NOT_ADD_UP') { toast.error(`These do not add up to the line: ${e.body.detail}`); return; }
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not record the decision');
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium text-text">{itemLabel(item)}</span>
        <span className="text-xs text-text-muted">{item.line_count} line{item.line_count === 1 ? '' : 's'}{item.effective_date ? ` · eff ${item.effective_date}` : ''}</span>
        <span className="ml-auto font-mono text-xs text-text">statement {fmtCents(item.net_due)}{item.policy_transaction_id ? ` · TRACON ${item.derived_net === null ? 'cannot work it out' : fmtCents(item.derived_net)}` : ''}</span>
      </div>
      {item.policy_transaction_id
        ? <div className="mt-1 text-xs text-text-muted">Proposed: <span className="text-text">{item.deal_name || 'a deal with no name on file'}</span> ({what(item)}). Not exact because {item.reason}.</div>
        : <div className="mt-1 text-xs text-danger">No deal proposed: {item.reason}.</div>}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="min-w-[18rem] flex-1 text-xs text-text-muted">
          Deal
          <select value={txn} onChange={(e) => { setTxn(e.target.value); setRefusal(null); }} className={selectCls}>
            <option value="">choose the deal these lines belong to</option>
            {choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </label>
        <Button variant="outline" onClick={() => decide('confirmed')} disabled={busy || !txn || (needsReason && !reason.trim())}>
          {txn ? `Confirm to ${chosen ? chosen.label.split(' · ')[0] : 'this deal'}` : 'Confirm'}
        </Button>
        <Button variant="outline" onClick={() => decide('rejected')} disabled={busy}>No such deal</Button>
      </div>
      {item.split ? (
        <div className="mt-2 rounded-card border border-border bg-surface p-2 text-xs text-text-muted">
          Two rows add up to this line to the cent:
          <ul className="mt-1">
            {item.split.rows.map((x) => (
              <li key={x.policy_transaction_id} className="text-text">
                {x.deal_name || 'a deal with no name on file'} · {x.type}{x.effective_date ? ` eff ${x.effective_date}` : ''}{x.description ? ` · ${x.description}` : ''} <span className="font-mono">net {fmtCents(x.derived_net)} · commission {fmtCents(x.derived_commission)}</span>
              </li>
            ))}
          </ul>
          <Button variant="outline" className="mt-2" onClick={() => decide('confirmed', item.split.policy_transaction_ids)} disabled={busy}>Confirm to both</Button>
        </div>
      ) : null}
      {txn ? (
        <label className="mt-2 block text-xs text-text-muted">
          Split with a second deal (only when the carrier netted two into this line; they must add up to it to the cent)
          <select value={txn2} onChange={(e) => setTxn2(e.target.value)} className={selectCls}>
            <option value="">no second deal</option>
            {choices.filter((c) => c.id !== txn).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          {txn2 ? <Button variant="outline" className="mt-2" onClick={() => decide('confirmed', [txn, txn2])} disabled={busy}>Confirm to {label(txn)?.split(' · ')[0] || 'the first'} and {label(txn2)?.split(' · ')[0] || 'the second'}</Button> : null}
        </label>
      ) : null}
      {refusal ? <Figures refusal={refusal} /> : null}
      {needsReason ? (
        <label className="mt-2 block text-xs text-text-muted">
          Why the statement and TRACON differ (kept beside both figures)
          <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
        </label>
      ) : null}
    </li>
  );
}

export default function StatementMatching({ proposal, unpaid, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!proposal) return null;
  const { exact, proposed, unmatched } = proposal;
  if (!exact.length && !proposed.length && !unmatched.length) return <p className="mt-2 text-xs text-text-muted">Every line on this statement is decided.</p>;
  const confirmExact = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/money/statements/${proposal.statement_id}/confirm-exact`, { items: exact.map((x) => ({ line_ids: x.line_ids, policy_transaction_id: x.policy_transaction_id })) });
      const opened = (r.confirmed || []).filter((c) => c.payable_leg && c.payable_leg.opened).length;
      const numbered = (r.confirmed || []).filter((c) => c.policy_number && c.policy_number.recorded).length;
      toast.success(`${(r.confirmed || []).length} exact match${(r.confirmed || []).length === 1 ? '' : 'es'} confirmed${opened ? `, ${opened} payable${opened === 1 ? '' : 's'} opened` : ''}${numbered ? `, ${numbered} policy number${numbered === 1 ? '' : 's'} recorded` : ''}.`);
      for (const f of r.refused || []) toast.info(`Not confirmed: ${f.detail || f.code}`);
      await onDone();
    } catch (e) {
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not confirm the exact matches');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 rounded-card border border-border bg-bg p-3">
      <div className="text-xs text-text-muted">
        Match these lines to deals: {exact.length} exact, {proposed.length} to check, {unmatched.length} with no deal.
      </div>
      {exact.length ? (
        <div className="mt-2">
          <ul className="divide-y divide-border">
            {exact.map((x) => (
              <li key={x.key} className="flex flex-wrap items-baseline gap-x-2 py-1.5 text-sm">
                <span className="text-text">{itemLabel(x)}</span>
                <span className="text-xs text-text-muted">to {x.deal_name} ({what(x)})</span>
                <span className="ml-auto font-mono text-xs text-text">{fmtCents(x.net_due)}</span>
              </li>
            ))}
          </ul>
          <Button onClick={confirmExact} disabled={busy} className="mt-2">{busy ? 'Confirming...' : `Confirm ${exact.length} exact match${exact.length === 1 ? '' : 'es'}`}</Button>
          <p className="mt-1 text-[11.5px] text-text-dim">Exact: the policy number agrees and the statement&apos;s amount equals what TRACON works out, to the cent.</p>
        </div>
      ) : null}
      {proposed.length || unmatched.length ? (
        <ul className="mt-2 divide-y divide-border border-t border-border">
          {[...proposed, ...unmatched].map((x) => <ItemRow key={x.key} statementId={proposal.statement_id} item={x} unpaid={unpaid} onDone={onDone} />)}
        </ul>
      ) : null}
    </div>
  );
}
